import cc from 'classcat';
import React, { useEffect, useRef, useState } from 'react';

import C from './VideoCard.css';

import { PlayCircleFilled } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import { localesMap } from 'utils/locales';
import { useLocale } from 'components/locale-switcher/LocaleSwitcher';

type VideoCardProps = {
  logo: string;
  videoSrc: string;
  hoverAutoPlayOn?: boolean;
  pause?: boolean;
  isInJamDetailsModal?: boolean;
  caption?: string;
} & React.DetailedHTMLProps<
  React.VideoHTMLAttributes<HTMLVideoElement>,
  HTMLVideoElement
>;

const VideoCard: React.FC<VideoCardProps> = ({
  logo,
  videoSrc,
  hoverAutoPlayOn = false,
  pause = true,
  isInJamDetailsModal = false,
  ...p
}) => {
  const [videoPlaying, setVideoPlaying] = useState(false);
  const ref = useRef<HTMLVideoElement | null>(null);

  const { locale: defaultLocale } = useLocale();

  const playVideo = () => {
    if (!videoPlaying) {
      ref?.current
        ?.play()
        .then((_) => {
          setVideoPlaying(true);
        })
        .catch((_) => {
          setVideoPlaying(false);
        });
    }
  };

  const pauseVideo = () => {
    if (videoPlaying) ref?.current?.pause();
    setVideoPlaying(false);
  };

  const handleOnMouseOver = (e: React.MouseEvent<HTMLVideoElement>) => {
    if (!hoverAutoPlayOn) return;
    playVideo();
  };

  const handleOnMouseOut = (e: React.MouseEvent<HTMLVideoElement>) => {
    if (!hoverAutoPlayOn) return;
    pauseVideo();
  };

  return (
    <div
      className={cc([
        C.container,
        hoverAutoPlayOn ? C.hoverable : C.nothoverable,
        isInJamDetailsModal ? C.inJamDetailsModal : '',
      ])}
    >
      {hoverAutoPlayOn ? (
        <>
          <img className={C.videoBanner} src={logo} alt='logo' />
          {p.children}
        </>
      ) : (
        <></>
      )}
      <video
        className={C.jamVideo}
        key={videoSrc}
        ref={ref}
        playsInline
        preload={hoverAutoPlayOn ? 'none' : 'auto'}
        muted={hoverAutoPlayOn} // Needs to be there to be able to play
        disablePictureInPicture
        onMouseOver={handleOnMouseOver}
        onMouseOut={handleOnMouseOut}
        onPlay={() => setVideoPlaying(true)}
        onPause={() => setVideoPlaying(false)}
        poster={hoverAutoPlayOn ? '' : logo}
        controlsList='nodownload'
        {...p}
      >
        <source src={videoSrc} type='video/mp4' />
        {p.caption &&
          Object.keys(localesMap).map((locale) => (
            <track
              key={locale}
              src={`/static/captions/${locale}/${p.caption}`}
              kind='subtitles'
              srcLang={locale}
              label={localesMap[locale]}
              default={locale === defaultLocale}
            />
          ))}
      </video>
      {!hoverAutoPlayOn ? (
        !videoPlaying ? (
          <IconButton size={'large'} className={C.active} onClick={playVideo}>
            <PlayCircleFilled style={{ fontSize: 64 }} />
          </IconButton>
        ) : (
          <></>
        )
      ) : (
        <></>
      )}
    </div>
  );
};
export default VideoCard;
